import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import useSeo from "@/hooks/useSeo";
import { JOBS, JOBS_EMAIL, EMPLOYER_INTRO } from "@/data/jobs";
import { COMPANY } from "@/i18n/translations";
import { ArrowRight, MapPin, Briefcase, Layers, Building2, Mail } from "lucide-react";

export default function Karriere() {
  useSeo({
    title: "Karriere bei DNA Automotive · Offene Stellen in Köln",
    description: "Fahrzeuge. Menschen. Leidenschaft. Offene Stellen bei DNA Automotive GmbH in Köln: Automobilverkäufer (m/w/d), Fahrer auf Minijob-Basis, Aushilfe Fahrzeuglogistik. Jetzt bewerben!",
    path: "/karriere",
    image: "/og/karriere.png",
    breadcrumbs: [{ name: "Karriere", url: "/karriere" }],
  });
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="bg-obsidian min-h-screen" data-testid="karriere-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-6 md:px-10 overflow-hidden grain">
        <div className="absolute inset-0 z-0 bg-obsidian-surface" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-obsidian via-obsidian/85 to-obsidian/60" />
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">DNA Automotive · Köln</p>
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-7xl leading-[0.9] max-w-5xl">
              Karriere bei<br /><span className="text-brand-bright">DNA Automotive</span>
            </h1>
            <p className="font-heading text-zinc-300 text-xl md:text-2xl mt-6 uppercase tracking-wide">
              Fahrzeuge. Menschen. Leidenschaft.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Arbeitgeber-Vorstellung */}
      <section className="px-6 md:px-10 pb-20" data-testid="karriere-employer">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">Über uns als Arbeitgeber</p>
            <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl leading-[0.95]">
              Wachse mit<br />DNA Automotive.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-zinc-300 leading-relaxed text-base md:text-lg max-w-3xl">{EMPLOYER_INTRO}</p>
            <div className="flex flex-wrap gap-6 mt-8 text-zinc-400 text-sm">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-brand-bright" />{COMPANY.name}</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-bright" />MotorWorld Köln-Rheinland</span>
            </div>
          </div>
        </div>
      </section>

      {/* Offene Stellen */}
      <section className="px-6 md:px-10 pb-24" data-testid="jobs-list">
        <div className="max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">Aktuelle Stellenangebote</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-12">
            Offene Positionen
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {JOBS.map((job, i) => (
              <motion.article
                key={job.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative flex flex-col bg-obsidian-surface border border-white/10 hover:border-brand-light/50 transition-all duration-500 overflow-hidden"
                data-testid={`job-card-${job.slug}`}
              >
                <div className="p-8 flex flex-col flex-1">
                  <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl leading-tight mb-6 group-hover:text-brand-bright transition-colors duration-500">
                    {job.title}
                  </h3>

                  <dl className="space-y-2.5 text-sm mb-6">
                    <div className="flex items-center gap-2.5 text-zinc-300">
                      <Briefcase className="w-4 h-4 text-brand-bright shrink-0" />
                      <dt className="text-zinc-500">Anstellungsart:</dt>
                      <dd className="text-white font-medium">{job.employmentType}</dd>
                    </div>
                    <div className="flex items-center gap-2.5 text-zinc-300">
                      <Layers className="w-4 h-4 text-brand-bright shrink-0" />
                      <dt className="text-zinc-500">Abteilung:</dt>
                      <dd className="text-white font-medium">{job.department}</dd>
                    </div>
                    <div className="flex items-center gap-2.5 text-zinc-300">
                      <MapPin className="w-4 h-4 text-brand-bright shrink-0" />
                      <dt className="text-zinc-500">Standort:</dt>
                      <dd className="text-white font-medium">{job.location}</dd>
                    </div>
                    <div className="flex items-center gap-2.5 text-zinc-300">
                      <Building2 className="w-4 h-4 text-brand-bright shrink-0" />
                      <dt className="text-zinc-500">Unternehmen:</dt>
                      <dd className="text-white font-medium">DNA Automotive GmbH</dd>
                    </div>
                  </dl>

                  <p className="text-zinc-400 text-sm leading-relaxed flex-1">{job.teaser}</p>

                  <Link
                    to={`/karriere/${job.slug}`}
                    data-testid={`job-view-${job.slug}`}
                    className="group/btn mt-8 inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bold uppercase tracking-wider text-xs px-6 py-4 hover:bg-brand hover:text-black hover:border-brand transition-colors"
                  >
                    Stelle ansehen
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-brand-light group-hover:w-full transition-all duration-700" />
              </motion.article>
            ))}
          </div>

          {/* Initiativ */}
          <div className="mt-16 border border-white/10 bg-obsidian-surface p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6" data-testid="karriere-initiativ">
            <div className="flex-1">
              <h2 className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl md:text-3xl mb-3">Nichts Passendes dabei?</h2>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
                Wir freuen uns auch über Initiativbewerbungen. Bewerbung per E-Mail an{" "}
                <a href={`mailto:${JOBS_EMAIL}`} className="text-brand-bright hover:underline">{JOBS_EMAIL}</a>
              </p>
            </div>
            <a
              href={`mailto:${JOBS_EMAIL}?subject=${encodeURIComponent("Initiativbewerbung – DNA Automotive")}`}
              className="inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover transition-colors whitespace-nowrap"
              data-testid="karriere-initiativ-btn"
            >
              <Mail className="w-4 h-4" />
              Jetzt bewerben
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton prefill="Hallo DNA Automotive, ich interessiere mich für eine Stelle." />
    </div>
  );
}
